/**
 * POP React Query Hooks
 *
 * Custom hooks for Personal Development Plans.
 * Story 6-5
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  popApi,
  type UpdatePopInput,
  type CreateProgressEntryInput,
} from '../../lib/popApi';

// Query keys
export const popKeys = {
  all: ['pop'] as const,
  details: () => [...popKeys.all, 'detail'] as const,
  detail: (employeeId: number) => [...popKeys.details(), employeeId] as const,
};

/**
 * Hook to fetch POP for employee
 * Returns null for 404 (no POP exists) without treating it as an error
 */
export function usePop(employeeId: number) {
  return useQuery({
    queryKey: popKeys.detail(employeeId),
    queryFn: async () => {
      try {
        return await popApi.getPopForEmployee(employeeId);
      } catch (error) {
        // 404 means no POP exists - return null instead of throwing
        if (error && typeof error === 'object' && 'status' in error && error.status === 404) {
          return null;
        }
        throw error;
      }
    },
    enabled: employeeId > 0,
  });
}

/**
 * Hook to update POP content
 */
export function useUpdatePop() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ employeeId, input }: { employeeId: number; input: UpdatePopInput }) =>
      popApi.updatePop(employeeId, input),
    onSuccess: (_, { employeeId }) => {
      queryClient.invalidateQueries({ queryKey: popKeys.detail(employeeId) });
    },
  });
}

/**
 * Hook to add progress entry
 */
export function useAddProgressEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      employeeId,
      input,
    }: {
      employeeId: number;
      input: CreateProgressEntryInput;
    }) => popApi.addProgressEntry(employeeId, input),
    onSuccess: (_, { employeeId }) => {
      queryClient.invalidateQueries({ queryKey: popKeys.detail(employeeId) });
    },
  });
}

/**
 * Hook to delete progress entry
 */
export function useDeleteProgressEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ employeeId, progressId }: { employeeId: number; progressId: number }) =>
      popApi.deleteProgressEntry(employeeId, progressId),
    onSuccess: (_, { employeeId }) => {
      queryClient.invalidateQueries({ queryKey: popKeys.detail(employeeId) });
    },
  });
}
